import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";

import { blankStyleOverrides, stylingOverrideReturns } from "../../../../../live-event/utils";
import { LanguagesAbbr, Session, SessionPreview, SessionTypesEnum, TranslateString } from "../../../../../../types/working-model";
import { Tag } from "../../../../../general-ui/tag/tag";
import { TypographyItem } from "../../../../../general-ui/typography-item/typography-item";
import { Button } from "../../../../../general-ui/button/button";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import { getTextValue, updateTranslateKey, displayAddToCalendarButton } from "../../../../../../utils/utils";
import { updateSessionCard } from "../../../../../../store/actions/admin/create-event";
import { EUploadOptions, ImageUploader, ImageUploadOptions } from "../../../../../general-ui/image-uploader/image-uploader";
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { useTranslate } from "../../../../../../i18n/useTranslationModules";
import { OptionalComponent } from "../../../../../../utils/optional-component";
import SessionTags from "../../../../../live-event/modules/agenda/session-tags";
import EditableTextDraftQuill from "../../../../../general-ui/editable-text/editable-text-draft-quill";
import useGetSessionTags from "../../../../../../utils/use-get-session-tags";
import { useGetTagText } from "../../../../../../hooks/agenda.hooks";
import { useGetGoToSessionButtonText } from "../../../../../../hooks/button-text.hooks";
import NavigationDropdown from "../../../../../general-ui/dropdown/navigation-dropdown";
import { EIcon } from "../../../../../general-ui/icon/icon";
import { setLiveSessions } from "../../../../../../store/actions/event/event-actions";
import { EditorSizes } from "../../../../../../types/template-layouts";

interface SessionCardProps {
	session: Session;
	stylingOverrides?: stylingOverrideReturns;
	template: string;
	defaultImage: string;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
	editDisabled?: boolean;
	addToCalendar?: boolean;
	isCalendarLayout?: boolean;
	featuredCard?: boolean;
}

const SessionCard: React.FC<SessionCardProps> = ({
	session,
	language,
	baseLanguage,
	stylingOverrides = blankStyleOverrides,
	template,
	defaultImage,
	editDisabled = false,
	isCalendarLayout = false,
	featuredCard = false
}) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const { liveSessions, translationUpdates } = useTypedSelector(state => state.LiveEventReducer);
	const showAddToCalBtn = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.show_add_to_cal_btn);
	const myListActive = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.personalizedAttendeeList);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);

	const [sessionCardQuillDescription, setSessionCardQuillDescription] = useState<string>('');
	const [sessionCardEdited, setSessionCardEdited] = useState(false);
	const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);

	const dispatch = useDispatch();
	const { t } = useTranslate(["homepage", "landing"]);
	const sessionTags = useGetSessionTags(workingEvent, session);

	const {
		moduleCustomHeadingOverrides,
		secondaryButtonOverrides
	} = stylingOverrides;

	const { description, title } = getTextValue(session, language);
	useEffect(() => {
		setSessionCardQuillDescription(t(`landing:agenda.session.${session.session}.description`, description));
	}, [description, session.session, t, translationUpdates]);

	function handleDefaultEditorChange(key: "description" | "title") {
		return (text: string) => {
			if (!token) return;
			const _session: Session = {
				...session,
				[key]: updateTranslateKey({
					translateString: session[key] as TranslateString,
					input: text,
					language,
					baseLanguage
				})
			};
			dispatch(updateSessionCard(_session, token));
		};
	}

	function handleQuillTextChange(text: string) {
		setSessionCardEdited(true);
		setSessionCardQuillDescription(text);
	}

	// waiting to update redux/db until editing is complete to prevent editor jumping unexpectedly
	const handleQuillDescriptionUpdate = () => {
		if (!sessionCardEdited || !token) return;

		let _session: Session = { ...session };
		_session = {
			...session,
			description: updateTranslateKey({
				translateString: session.description as TranslateString,
				input: sessionCardQuillDescription,
				language,
				baseLanguage
			})
		};

		dispatch(updateSessionCard(_session, token));
		setSessionCardEdited(false);
	};

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		// TODO: update model to save original image file as well as edited
		const _session: Session = {
			...session,
			image: image.edited
		};
		if (token) {
			dispatch(updateSessionCard(_session, token));
		}
	}, [dispatch, session, token]);

	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;
	const isBreakoutRooms = session.session_type === SessionTypesEnum.breakoutRooms;
	const isMobile = editorSize === EditorSizes.mobile;
	const isTablet = editorSize === EditorSizes.tablet;
	const isDesktop = editorSize === EditorSizes.desktop;

	const showAddToCalendarButton = useMemo(() => {
		if (!session) return false;
		return displayAddToCalendarButton(session, showAddToCalBtn);
	}, [session, showAddToCalBtn]);

	useEffect(() => {
		dispatch(setLiveSessions(workingEvent?.playbackUrls || {}));
	}, [dispatch, workingEvent?.playbackUrls]);

	const isBroadcastLive = useMemo(() => {
		// Broadcast sessions are live if the broadcast stream is actually streaming, not the date
		return liveSessions.includes(session.uuid);
	}, [session, liveSessions]);

	const isBreakoutRoomsLive = (session: Session | SessionPreview) => {
		if (!session.timestamp || !session.end_timestamp) return false;
		const date = Date.now();
		const timestamp = new Date(session.timestamp);
		const end_timestamp = new Date(session.end_timestamp);
		if (timestamp.getTime() < date && end_timestamp.getTime() > date) {
			return true;
		} else {
			return false;
		}
	};

	const isSessionLive = (session: Session | SessionPreview) => {
		if (isBroadcast) {
			return isBroadcastLive;
		} else if (isBreakoutRooms) {
			return isBreakoutRoomsLive(session);
		} else {
			// On Demand
			return false;
		}
	};

	const buttonText = useGetGoToSessionButtonText(session, isSessionLive(session));

	const disableSessionThumbnails = workingEvent?.settings?.disable_session_thumbnails;

	const shouldShowTimestamp = (isBroadcast || isBreakoutRooms);

	const timestampText = useGetTagText({
		session_type: session.session_type,
		shouldShowTimestamp,
		timestamp: session.timestamp,
		language,
		tracks: session.tracks
	});

	const renderStandardSessionCard = () => (
		<div
			className={
				classNames(
					"agenda-session-card",
					{
						editDisabled,
						'no-image': disableSessionThumbnails,
						'featured-card': featuredCard,
						'live': isSessionLive(session),
					}
				)
			}
		>
			<div
				className={
					classNames(
						"agenda-session-card-image",
						moduleCustomHeadingOverrides,
						{ 'no-image': disableSessionThumbnails }
					)
				}
			>
				<OptionalComponent display={!disableSessionThumbnails}>
					{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
				</OptionalComponent>
				{isSessionLive(session) && (
					<div className="agenda-session-card-tags-wrapper">
						<Tag
							classNameTag={classNames("tag-container-live", moduleCustomHeadingOverrides)}
							text={t("homepage:live", "")}
						/>
					</div>
				)}
				<div className='edit-image-button'>
					<OptionalComponent display={!!session?.image && !disableSessionThumbnails}>
						<button className="round edit-button" onClick={() => setImageModalOpen(EUploadOptions.edit)}><Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} /></button>
					</OptionalComponent>
					<OptionalComponent display={!disableSessionThumbnails}>
						<button className="round edit-button replace-image" onClick={() => setImageModalOpen(EUploadOptions.upload)}>
							<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
						</button>
					</OptionalComponent>
				</div>
			</div>
			<div className="agenda-session-card-content">
				<div className="agenda-session-card-text-content">
					<TypographyItem
						className={classNames('agenda-session-card-timestamp evt-typography-caption-1 text-ellipsis', moduleCustomHeadingOverrides)}
					>
						{timestampText}
					</TypographyItem>
					<EditableTextSlim
						tagType="h3"
						className="agenda-session-card-title agenda-session-editable-title"
						onChange={handleDefaultEditorChange("title")}
						value={title}
					/>
					<EditableTextSlim
						tagType="p"
						className="agenda-session-card-description"
						onChange={handleDefaultEditorChange("description")}
						value={description}
					/>
				</div>
				<div className="agenda-session-card-buttons">
					<Button
						classButton={classNames(
							"primary",
							secondaryButtonOverrides)}
						template={template}
						onClick={() => null}
					>
						{buttonText}
					</Button>

					{myListActive && (
						<Button
							classButton={classNames(
								"secondary with-svg",
								secondaryButtonOverrides)}
							template={template}
							title={t('homepage:Add to my list', "")}
						>
							<Icon name={ICONS.ADD} color="" size={22} />
						</Button>
					)}

					{showAddToCalendarButton ?
						<Button
							onClick={() => null}
							classButton={classNames("secondary with-svg", secondaryButtonOverrides)}
							template={template}
						>
							<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
						</Button>
						: null
					}
				</div>
			</div>
		</div>
	);

	const renderCalendarListViewSessionCard = (): JSX.Element => {
		return (
			<div className={classNames("agenda-session-card", { 'featured-card': featuredCard })} onBlur={handleQuillDescriptionUpdate}>
				<OptionalComponent display={!disableSessionThumbnails}>
					<div className={classNames("agenda-session-card-image", moduleCustomHeadingOverrides)}>
						{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
						<div className='edit-image-button'>
							<OptionalComponent display={!!session?.image}>
								<button className="round edit-button" onClick={() => setImageModalOpen(EUploadOptions.edit)}><Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} /></button>
							</OptionalComponent>
							<button className="round edit-button replace-image" onClick={() => setImageModalOpen(EUploadOptions.upload)}>
								<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
							</button>
						</div>
					</div>
				</OptionalComponent>
				<div className="agenda-session-card-content">
					<div className="agenda-session-card-info evt-typography-caption-2 text-ellipsis">
						{timestampText}
					</div>
					<EditableTextSlim
						tagType="h3"
						className="agenda-session-card-title agenda-session-editable-title evt-typography-h4 text-ellipsis"
						onChange={handleDefaultEditorChange("title")}
						value={title}
					/>
					<EditableTextDraftQuill
						className="agenda-session-card-description evt-typography-subtitle-b3"
						value={sessionCardQuillDescription}
						onChange={handleQuillTextChange}
						useNew
						displayOnLoading
					/>
					<SessionTags tags={sessionTags} />
				</div>
				<div className="agenda-session-card-buttons">
					{isTablet && (showAddToCalendarButton || myListActive) &&
						<div className="action-menu">
							<NavigationDropdown
								title=""
								className="menu-dropdown"
								buttonClassName="evt-button secondary with-svg"
								renderCustomIcon={() => <EIcon name="more-horiz" size={22} />}
							>
								<></>
							</NavigationDropdown>
						</div>
					}

					<OptionalComponent display={!isTablet} >
						<>
							{showAddToCalendarButton && (
								<Button
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
								>
									<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
								</Button>
							)}
							{myListActive && (
								<Button
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
									title={t('homepage:Add to my list', "")}
								>
									<Icon name={ICONS.ADD} color="" size={22} />
								</Button>
							)}
							<Button
								classButton={classNames("primary", secondaryButtonOverrides)}
								template={template}
							>
								{buttonText}
							</Button>
						</>
					</OptionalComponent>
				</div>
			</div>
		);
	};

	return (<>
		{isCalendarLayout ? renderCalendarListViewSessionCard() : renderStandardSessionCard()}

		<ImageUploader
			originalImage={session?.image}
			onSave={saveImage}
			onClose={() => setImageModalOpen(null)}
			open={imageModalOpen}
		/>
	</>);
};

export default SessionCard;